import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PropTypes } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import Joi from 'joi';

import {
    useGetPromotersQuery,
    useGetRolesQuery,
    useGetSGPsQuery,
    useInviteUserMutation,
} from 'api/main';
import { userSelectors } from 'reducers';
import { useMyPromoter, useUserRoleMatchCategory } from 'utils/hooks';
import { userSchemas } from 'schemas';
import { getRenderRoleName, validateSchema } from 'utils/helpers';
import {
    CognitoErrors,
    EMAIL_VALIDATION_OPTIONS,
    InternalErrors,
    investorErrors,
    inviteFormType,
    NAME_REGEX,
    promoterRoles,
    providerRoles,
    roleCategories,
    RoleNames,
    servicerRoles,
} from 'utils/constants';
import { titles } from 'utils/formData';
import { texts } from 'utils/languages/fr';

import TunnelBox from 'containers/tunnel/common/TunnelBox';
import {
    ErrorMessage,
    CustomSelect,
    SubmitButton,
    CustomTextField,
    Loader,
    CustomPhoneField,
    CheckboxLabel,
} from 'components';
import { MenuItem, Typography, FormControl, Box } from '@mui/material';

const getProviderDefaultValue = type => (type === inviteFormType.PROVIDER ? '' : undefined);

const renderOption = opt => (
    <MenuItem key={opt} value={opt}>
        {opt}
    </MenuItem>
);

const renderSGP = sgp => (
    <MenuItem key={sgp.id} value={sgp.id}>
        {sgp.name}
    </MenuItem>
);

const renderRole = role => (
    <MenuItem key={role.id} value={role.id}>
        {getRenderRoleName(role)}
    </MenuItem>
);

const InviteUser = ({ title, type }) => {
    const dispatch = useDispatch();
    const userIsProvider = useUserRoleMatchCategory(roleCategories.PROVIDERS);
    const userIsPromoter = useUserRoleMatchCategory(roleCategories.PROMOTERS);
    const internalUser = useSelector(userSelectors.getInternalUser);

    const { data: { sgps = [] } = { sgps: [], lastPage: 1 }, isLoading: sgpsLoading } =
        useGetSGPsQuery({}, { skip: type !== inviteFormType.PROVIDER || userIsProvider });
    const {
        data: { promoters = [] } = { promoters: [], lastPage: 1 },
        isLoading: promotersLoading,
    } = useGetPromotersQuery({}, { skip: type !== inviteFormType.PROMOTER || userIsPromoter });
    const { data: roles = [], isLoading: rolesLoader } = useGetRolesQuery();
    const [inviteUser, { isLoading: inviteLoader, isSuccess, isError, error }] =
        useInviteUserMutation();
    const [phoneError, setPhoneError] = useState('');
    const { promoter, isLoading: promoterLoading } = useMyPromoter();

    const availableRoles = useMemo(() => {
        switch (type) {
            case inviteFormType.SERVICER:
                return roles.filter(
                    r =>
                        r.category?.name === roleCategories.SERVICERS &&
                        servicerRoles.includes(r.name)
                );
            case inviteFormType.PROVIDER:
                return roles.filter(
                    r =>
                        r.category?.name === roleCategories.PROVIDERS &&
                        providerRoles.includes(r.name)
                );
            case inviteFormType.PROMOTER:
                return roles.filter(
                    r =>
                        r.category?.name === roleCategories.PROMOTERS &&
                        promoterRoles.includes(r.name)
                );
            case inviteFormType.CIF:
                return roles.filter(
                    r => r.name === RoleNames.ADMIN_CIF || r.name === RoleNames.COMMERCIALISATEUR
                );
            default:
                return [];
        }
    }, [roles, type]);

    const [form, setForm] = useState({
        title: titles[0],
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: '',
        role: '',
        tag: type === inviteFormType.CIF ? '' : undefined,
        sgp: userIsProvider ? internalUser.sgp?.id : getProviderDefaultValue(type),
        promoter: userIsPromoter ? promoter?.id : undefined,
        marketingCertified: false,
    });
    const [errors, setErrors] = useState({
        firstName: '',
        lastName: '',
        email: '',
        marketingCertified: '',
        role: '',
    });

    const handlePhoneChange = useCallback(phone => {
        setForm(oldValue => ({ ...oldValue, phoneNumber: phone }));
    }, []);

    useEffect(() => {
        if (roles.length) {
            setForm(oldValue => ({
                ...oldValue,
                role: oldValue.role || availableRoles[0]?.id,
                promoter: promoter?.id,
            }));
        }
    }, [roles, availableRoles, promoter]);

    const schema = useMemo(
        () => ({
            firstName: Joi.string().required().trim().min(2).max(120).pattern(NAME_REGEX).messages({
                'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_PRENOM,
                'string.min': texts.FIRST_NAME_LENGTH,
                'string.max': texts.FIRST_NAME_LENGTH,
                'string.pattern.base': texts.FIRST_NAME_PATTERN,
            }),
            lastName: Joi.string().required().trim().min(2).max(120).pattern(NAME_REGEX).messages({
                'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_NOM,
                'string.min': texts.LAST_NAME_LENGTH,
                'string.max': texts.LAST_NAME_LENGTH,
                'string.pattern.base': texts.LAST_NAME_PATTERN,
            }),
            email: Joi.string().required().email(EMAIL_VALIDATION_OPTIONS).messages({
                'string.empty': texts.SAISIR_LE_CHAMP,
                'string.email': texts.ADRESSE_EMAIL_SAISIE_PAS_VALIDE,
            }),
            phoneNumber: Joi.string().required().messages({
                'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_TELEPHONE,
            }),
            marketingCertified:
                type === inviteFormType.CIF
                    ? Joi.boolean().valid(true).messages({ 'any.only': texts.CHOCHER_CASE })
                    : Joi.any(),
            sgp:
                type === inviteFormType.PROVIDER
                    ? Joi.number().required().messages({
                          'number.required': texts.CHOISIR_UNE_OPTION,
                          'number.base': texts.CHOISIR_UNE_OPTION,
                      })
                    : Joi.any(),
            promoter:
                type === inviteFormType.PROMOTER
                    ? Joi.number().required().messages({
                          'number.required': texts.CHOISIR_UNE_OPTION,
                          'number.base': texts.CHOISIR_UNE_OPTION,
                      })
                    : Joi.any(),
        }),
        [type]
    );

    const handleChange = useCallback(
        key => e => {
            const { value } = e.target;
            setForm(oldValue => ({ ...oldValue, [key]: value }));
            if (key === 'email') {
                setErrors(prevErrors => ({ ...prevErrors, email: '' }));
            }
        },
        []
    );

    const handleMarketingCertifiedChange = useCallback(e => {
        const { checked } = e.target;
        setForm(oldValue => ({ ...oldValue, marketingCertified: checked }));
    }, []);

    const formIsValid = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, form);
        setErrors(newErrors);

        return valid;
    }, [form, schema]);

    const handleInvite = useCallback(() => {
        if (formIsValid()) {
            inviteUser(userSchemas.inviteUser(form, type));
        }
    }, [form, formIsValid, inviteUser, type]);

    useEffect(() => {
        if (isSuccess) {
            if (!userIsProvider && type === inviteFormType.SERVICER) {
                dispatch(push('/servicer-users'));
            } else if (userIsProvider || type === inviteFormType.PROVIDER) {
                dispatch(push('/provider-users'));
            } else if (type === inviteFormType.CIF) {
                dispatch(push('/distributor-users'));
            } else if (type === inviteFormType.PROMOTER) {
                dispatch(push('/promoter-users'));
            }
        } else if (isError) {
            const details =
                (error?.data?.detail?.split('\n') ||
                    error?.data['hydra:description']?.split('\n')) ??
                [];
            if (
                error?.data?.code === CognitoErrors.USERNAME_EXISTS ||
                details.includes(InternalErrors.USERNAME_EXISTS) ||
                details.includes(InternalErrors.EMAIL_EXISTS_2)
            ) {
                setErrors(prevErrors => ({ ...prevErrors, email: texts.EMAIL_DUPLICATE }));
            }
            if (
                details === investorErrors.INVALID_PHONE ||
                details.some(detail => detail.includes('is not a valid phone number'))
            ) {
                setPhoneError(texts.INVALID_PHONE);
                setErrors(prevErrors => ({ ...prevErrors, phoneNumber: texts.INVALID_PHONE }));
            }
        }
    }, [dispatch, error, isSuccess, isError, type, userIsProvider]);

    return (
        <React.Fragment>
            <Typography variant="h5" align="left" gutterBottom>
                {title}
            </Typography>
            <TunnelBox mt="30px">
                <Loader loading={rolesLoader || sgpsLoading || promotersLoading || promoterLoading}>
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        width="50%"
                        margin="0 auto"
                    >
                        <CustomSelect
                            fullWidth
                            label={texts.CIVILITE}
                            value={form.title}
                            onChange={handleChange('title')}
                        >
                            {titles.map(renderOption)}
                        </CustomSelect>
                        <CustomTextField
                            fullWidth
                            label={texts.PRENOM}
                            value={form.firstName}
                            onChange={handleChange('firstName')}
                            error={errors.firstName}
                        />
                        <CustomTextField
                            fullWidth
                            label={texts.NOM}
                            value={form.lastName}
                            onChange={handleChange('lastName')}
                            error={errors.lastName}
                        />
                        <CustomTextField
                            fullWidth
                            inputProps={{
                                autoCapitalize: 'off',
                                autoComplete: 'off',
                                spellCheck: 'off',
                                autoCorrect: 'off',
                            }}
                            label={texts.E_MAIL}
                            value={form.email}
                            onChange={handleChange('email')}
                            error={errors.email}
                        />
                        <CustomPhoneField
                            fullWidth
                            label={texts.TELEPHONE}
                            value={form.phoneNumber}
                            onChange={handlePhoneChange}
                            error={errors.phoneNumber || phoneError}
                        />
                        <CustomSelect
                            fullWidth
                            label={texts.SELECTIONNEZ_LE_ROLE}
                            value={form.role}
                            onChange={handleChange('role')}
                            error={errors.role}
                        >
                            {availableRoles.map(renderRole)}
                        </CustomSelect>
                        {type === inviteFormType.PROVIDER && !userIsProvider && (
                            <CustomSelect
                                fullWidth
                                label={texts.SGP_PROVIDER}
                                value={form.sgp}
                                onChange={handleChange('sgp')}
                                error={errors.sgp}
                            >
                                {sgps.map(renderSGP)}
                            </CustomSelect>
                        )}
                        {type === inviteFormType.PROMOTER && !userIsPromoter && (
                            <CustomSelect
                                fullWidth
                                label={texts.PROMOTER}
                                value={form.promoter}
                                onChange={handleChange('promoter')}
                                error={errors.promoter}
                            >
                                {promoters.map(renderSGP)}
                            </CustomSelect>
                        )}
                        {type === inviteFormType.CIF && (
                            <React.Fragment>
                                <CustomTextField
                                    fullWidth
                                    label={texts.COMPLEMENT}
                                    value={form.tag}
                                    onChange={handleChange('tag')}
                                />
                                <FormControl fullWidth>
                                    <CheckboxLabel
                                        onChange={handleMarketingCertifiedChange}
                                        value={form.marketingCertified}
                                        checked={form.marketingCertified}
                                        label={texts.INVITE_DISTRIBUTOR_CERTIFIED}
                                    />
                                    {errors.marketingCertified && (
                                        <ErrorMessage message={errors.marketingCertified} />
                                    )}
                                </FormControl>
                            </React.Fragment>
                        )}
                    </Box>
                </Loader>
            </TunnelBox>
            <SubmitButton
                text={texts.CREATE}
                onClick={handleInvite}
                disabled={inviteLoader}
                loading={inviteLoader}
            />
        </React.Fragment>
    );
};

InviteUser.propTypes = {
    title: PropTypes.string.isRequired,
    type: PropTypes.oneOf([
        inviteFormType.CIF,
        inviteFormType.SERVICER,
        inviteFormType.PROMOTER,
        inviteFormType.PROVIDER,
    ]).isRequired,
};

export default InviteUser;
